/**
 * Knowledge capture client for the AI Cortex.
 *
 * Sends structured interaction entries to the backend knowledge store
 * asynchronously (fire-and-forget) so the UI is never blocked.
 * Follows the same timeout / error-handling pattern as
 * {@link module:gibbs/text-to-action/docRetriever}.
 *
 * @module gibbs/cortex/knowledgeCapture
 */

/** Backend endpoint for knowledge capture. */
let captureEndpoint = '/services/cortex/capture/';

/**
 * Override the capture endpoint (for testing).
 * @param {string} url
 */
export function setCaptureEndpoint(url) {
    captureEndpoint = url;
}

/** Timeout for capture requests in milliseconds. */
const CAPTURE_TIMEOUT_MS = 2000;

/**
 * ID of the most recent capture response, used to attach feedback.
 * @type {string|null}
 */
let lastCaptureId = null;

/**
 * Return the ID of the last captured entry.
 * @returns {string|null}
 */
export function getLastCaptureId() {
    return lastCaptureId;
}

/**
 * Build a structured knowledge-capture entry.
 *
 * @param {string} userMessage - The user's prompt
 * @param {string} responseType - e.g. 'action', 'workflow', 'chat'
 * @param {string} domain - 'discovery' | 'clinical' | 'corporate'
 * @param {string[]} actionIds - IDs of actions executed
 * @param {string} resultSummary - Brief summary of the response
 * @param {Object} [metadata={}] - Additional metadata
 * @returns {Object} Structured entry ready for POST
 */
export function buildCaptureEntry(
    userMessage,
    responseType,
    domain,
    actionIds,
    resultSummary,
    metadata = {}
) {
    return {
        prompt: userMessage,
        responseType,
        domain,
        actionIds,
        resultSummary,
        timestamp: new Date().toISOString(),
        ...metadata,
    };
}

/**
 * Send a knowledge-capture entry to the backend.
 *
 * Fire-and-forget: the returned promise resolves quickly but the
 * caller is not expected to await it.  On failure the error is
 * logged and silently swallowed (decision D5).
 *
 * @param {Object} entry - Structured entry from buildCaptureEntry
 * @returns {Promise<void>}
 */
export async function captureInteraction(entry) {
    const controller = new AbortController();
    const timer = setTimeout(
        () => controller.abort(),
        CAPTURE_TIMEOUT_MS
    );

    try {
        const response = await fetch(captureEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(entry),
            signal: controller.signal,
        });

        clearTimeout(timer);

        if (!response.ok) {
            console.warn(
                '[knowledgeCapture] Capture endpoint returned'
                + ` HTTP ${response.status}`
            );
            return;
        }

        const data = await response.json();
        if (data && data.id) {
            lastCaptureId = data.id;
        }
    } catch (err) {
        clearTimeout(timer);
        const isTimeout = err.name === 'AbortError';
        console.warn(
            `[knowledgeCapture] ${isTimeout
                ? `Timed out after ${CAPTURE_TIMEOUT_MS}ms`
                : `Network error: ${err.message}`}`
        );
    }
}

/**
 * Submit thumbs-up / thumbs-down feedback for a captured entry.
 *
 * @param {string} entryId - ID of the knowledge entry
 * @param {'up'|'down'} feedback - User feedback value
 * @returns {Promise<void>}
 */
export async function submitFeedback(entryId, feedback) {
    const controller = new AbortController();
    const timer = setTimeout(
        () => controller.abort(),
        CAPTURE_TIMEOUT_MS
    );

    try {
        const url = `${captureEndpoint}${entryId}/feedback/`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ feedback }),
            signal: controller.signal,
        });

        clearTimeout(timer);

        if (!response.ok) {
            console.warn(
                '[knowledgeCapture] Feedback endpoint returned'
                + ` HTTP ${response.status}`
            );
        }
    } catch (err) {
        clearTimeout(timer);
        const isTimeout = err.name === 'AbortError';
        console.warn(
            `[knowledgeCapture] Feedback ${isTimeout
                ? `timed out after ${CAPTURE_TIMEOUT_MS}ms`
                : `error: ${err.message}`}`
        );
    }
}
